import React, { useEffect, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import MinimalFooter from './components/MinimalFooter';
import ErrorBoundary from './components/ErrorBoundary';
import AdminBreadcrumb from './components/admin/AdminBreadcrumb';
import { useAuth } from './context/AuthContext';
import { useLayout } from './context/LayoutContext';

// Lazy load public components
const Home = React.lazy(() => import('./components/Home'));
const JobBoard = React.lazy(() => import('./components/JobBoard'));
const VirtualInternship = React.lazy(() => import('./components/VirtualInternship'));
const VirtualInternshipBoard = React.lazy(() => import('./components/VirtualInternshipBoard'));
const JobListing = React.lazy(() => import('./components/JobListing'));
const Interview = React.lazy(() => import('./components/Interview'));
const InterviewFeedback = React.lazy(() => import('./components/InterviewFeedback'));
const InterviewReport = React.lazy(() => import('./components/InterviewReport'));
const JobApplication = React.lazy(() => import('./components/JobApplication'));
const CareerCoach = React.lazy(() => import('./components/CareerCoach'));
const CareerCourses = React.lazy(() => import('./components/CareerCourses'));
const CareerTools = React.lazy(() => import('./components/CareerTools'));
const InterviewPrep = React.lazy(() => import('./components/InterviewPrep'));
const NetworkingGuide = React.lazy(() => import('./components/NetworkingGuide'));
const ElevatorPitchBuilder = React.lazy(() => import('./components/ElevatorPitchBuilder'));
const NetworkBuilder = React.lazy(() => import('./components/NetworkBuilder'));
const CompanyDetail = React.lazy(() => import('./components/CompanyDetail'));
const CompanyHome = React.lazy(() => import('./components/CompanyHome'));
const CompanyBoard = React.lazy(() => import('./components/CompanyBoard'));
const IndustryDetail = React.lazy(() => import('./components/IndustryDetail'));
const CollectionDetail = React.lazy(() => import('./components/CollectionDetail'));
const AuthTokenHandler = React.lazy(() => import('./components/auth/AuthTokenHandler'));
const CareerAssessment = React.lazy(() => import('./components/CareerAssessment'));
const MyActivities = React.lazy(() => import('./components/MyActivities'));
const CareerAssessmentResults = React.lazy(() => import('./components/CareerAssessmentResults'));
const EducatorDashboard = React.lazy(() => import('./components/EducatorDashboard'));
const TrainingOpportunities = React.lazy(() => import('./components/TrainingOpportunities'));
const CareerOpportunities = React.lazy(() => import('./components/CareerOpportunities'));
const EducationPlanner = React.lazy(() => import('./components/EducationPlanner'));

// Lazy load the new TrackProgress component
const TrackProgress = React.lazy(() => import('./components/TrackProgress'));
// Lazy load the new CareerAssessmentReport component
const CareerAssessmentReport = React.lazy(() => import('./components/CareerAssessmentReport'));

// Lazy load admin components
const AdminLogin = React.lazy(() => import('./components/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const JobEditor = React.lazy(() => import('./components/admin/JobEditor'));
const JobList = React.lazy(() => import('./components/admin/JobList'));
const JobDataUpload = React.lazy(() => import('./components/admin/JobDataUpload'));
const QuestionEditor = React.lazy(() => import('./components/admin/QuestionEditor'));
const CompanyEditor = React.lazy(() => import('./components/admin/CompanyEditor'));
const CompanyList = React.lazy(() => import('./components/admin/CompanyList'));
const IndustriesPage = React.lazy(() => import('./components/admin/IndustriesPage'));
const IndustryEditor = React.lazy(() => import('./components/admin/IndustryEditor'));
const CareerList = React.lazy(() => import('./components/admin/CareerList'));
const CareerEdit = React.lazy(() => import('./components/admin/CareerEdit'));
const CollectionList = React.lazy(() => import('./components/admin/CollectionList'));
const CollectionEdit = React.lazy(() => import('./components/admin/CollectionEdit'));
const CollectionManage = React.lazy(() => import('./components/admin/CollectionManage'));
const CourseList = React.lazy(() => import('./components/admin/CourseList'));
const CourseEditor = React.lazy(() => import('./components/admin/CourseEditor'));
const CoursePlayer = React.lazy(() => import('./components/admin/CoursePlayer'));
const LessonList = React.lazy(() => import('./components/admin/LessonList'));
const LessonEditor = React.lazy(() => import('./components/admin/LessonEditor'));
const CourseGenerator = React.lazy(() => import('./components/admin/CourseGenerator'));
const SectionList = React.lazy(() => import('./components/admin/SectionList'));
const SectionEditor = React.lazy(() => import('./components/admin/SectionEditor'));
const PartnerList = React.lazy(() => import('./components/admin/PartnerList'));
const PartnerEditor = React.lazy(() => import('./components/admin/PartnerEditor'));
const PortalList = React.lazy(() => import('./components/admin/PortalList'));
const PortalEditor = React.lazy(() => import('./components/admin/PortalEditor'));
const PageManager = React.lazy(() => import('./components/admin/PageManager'));
const PageContent = React.lazy(() => import('./components/pages/PageContent'));
// Badge admin components
const BadgeList = React.lazy(() => import('./components/admin/BadgeList'));
const BadgeEditor = React.lazy(() => import('./components/admin/BadgeEditor'));
const BadgeAwardManager = React.lazy(() => import('./components/admin/BadgeAwardManager'));
const BadgeAssertionList = React.lazy(() => import('./components/admin/BadgeAssertionList'));
const BadgeVerification = React.lazy(() => import('./components/BadgeVerification'));

// Lazy load portal components
const PortalRouter = React.lazy(() => import('./components/PortalRouter'));

// Add new Virtual Internship components
const VirtualInternshipMain = React.lazy(() => import('./components/virtualinternship/VirtualInternshipMain'));
//const PanoramicViewer = React.lazy(() => import('./components/virtualinternship/PanoramicViewer'));
// Comment out ActivityPlayer import
const ActivityPlayer = React.lazy(() => import('./components/virtualinternship/ActivityPlayer'));

// Lazy load admin components
const InternshipList = React.lazy(() => import('./components/admin/virtualinternship/InternshipList'));
const InternshipBuilder = React.lazy(() => import('./components/admin/virtualinternship/InternshipBuilder'));
const VirtualInternshipDashboard = React.lazy(() => import('./components/admin/virtualinternship/VirtualInternshipDashboard'));
const LocationList = React.lazy(() => import('./components/admin/virtualinternship/LocationList'));
const LocationEditor = React.lazy(() => import('./components/admin/virtualinternship/LocationEditor'));
const HotspotEditor = React.lazy(() => import('./components/admin/virtualinternship/HotspotEditor'));
const ActivityEditor = React.lazy(() => import('./components/admin/virtualinternship/ActivityEditor'));
const ActivityManagement = React.lazy(() => import('./components/admin/activities/ActivityManagement'));
const ActivityBuilder = React.lazy(() => import('./components/admin/activities/ActivityBuilder'));
const ActivityPreview = React.lazy(() => import('./components/admin/virtualinternship/ActivityPreview'));

// Simulation Admin Components
const SimulationList = React.lazy(() => import('./components/admin/simulations/SimulationList'));
const SimulationEditor = React.lazy(() => import('./components/admin/simulations/SimulationEditor'));
const ManageStudents = React.lazy(() => import('./components/admin/ManageStudents'));

// Reports Components
const ReportDashboard = React.lazy(() => import('./components/admin/reports/ReportDashboard'));

// --- Add Test Page Import --- 
const ActivityTestPage = React.lazy(() => import('./pages/ActivityTestPage'));

// --- Add New Auth Page Imports ---
const LoginTypeSelection = React.lazy(() => import('./components/auth/LoginTypeSelection'));
const StudentLoginForm = React.lazy(() => import('./components/auth/StudentLoginForm'));
const UserLoginForm = React.lazy(() => import('./components/auth/UserLoginForm'));

// Loading component for suspense fallback
const Loading = () => {
  return (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);
};

// Helper component for protected routes
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isLoggedIn, isAdmin, loading, setShowLoginModal, setLoginRedirectPath } = useAuth(); // Add modal setters
  const navigate = useNavigate();
  const location = useLocation(); 

  useEffect(() => {
    if (!loading) {
      const adminToken = localStorage.getItem('adminToken');
      const hasAdminToken = !!adminToken;

      // Check for token in URL parameters
      const urlParams = new URLSearchParams(location.search);
      const urlToken = urlParams.get('token');
      const authSuccess = urlParams.get('auth_success');
      const alreadyAuth = urlParams.get('already_auth');
      
      console.log('ProtectedRoute: Checking authentication - isLoggedIn:', isLoggedIn, 'hasAdminToken:', hasAdminToken, 'urlToken:', !!urlToken);

      if (adminOnly && hasAdminToken) {
        return;
      }

      if (!isLoggedIn && !hasAdminToken && !urlToken) {
        console.log('ProtectedRoute: Not logged in, redirecting to login page.');
        setLoginRedirectPath(location.pathname + location.search); // Store the path string
        navigate('/login', { state: { from: location }, replace: true }); 
      } else if (adminOnly && !isAdmin && !hasAdminToken) {
        console.log('ProtectedRoute: Not an admin, redirecting to dashboard.');
        navigate('/dashboard', { replace: true }); 
      }
    }
  }, [loading, isLoggedIn, isAdmin, adminOnly, navigate, location, setLoginRedirectPath]); // Removed setShowLoginModal

  if (loading) {
    return <Loading />;
  }

  // Determine if user has admin access from localStorage
  const adminToken = localStorage.getItem('adminToken');
  const hasAdminToken = !!adminToken;

  // Check for token in URL parameters
  const urlParams = new URLSearchParams(location.search);
  const urlToken = urlParams.get('token');
  const authSuccess = urlParams.get('auth_success');
  const alreadyAuth = urlParams.get('already_auth');
  const hasUrlToken = urlToken && (authSuccess === 'true' || alreadyAuth === 'true');

  // Render children only if:
  // 1. Logged in as student OR has admin token OR has URL token AND
  // 2. For admin routes: either isAdmin from context OR has admin token
  const canAccess = (isLoggedIn || hasAdminToken || hasUrlToken) && (!adminOnly || isAdmin || hasAdminToken);

  console.log('ProtectedRoute: canAccess:', canAccess, 'isLoggedIn:', isLoggedIn, 'hasAdminToken:', hasAdminToken, 'hasUrlToken:', hasUrlToken);

  // If not logged in, useEffect will trigger the modal, so return null to prevent rendering children.
  // If logged in but not admin for adminOnly route, useEffect will navigate away, return null.
  return canAccess ? children : null;
};

// Create a separate component for the app content
const AppContent = () => {
  const location = useLocation();
  const { showHeader, showFooter, showMinimalFooter, layoutMode, isMinimal } = useLayout();
  
  // Debug layout state
  console.log('App: Layout state:', {
    layoutMode,
    showHeader,
    showFooter,
    showMinimalFooter,
    isMinimal,
    pathname: location.pathname,
    search: location.search
  });
  // --- Restore Portal Logic --- START
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true); // Start as true
  const [isPortalDomain, setIsPortalDomain] = useState(false);
  // --- Restore Portal Logic --- END

  // --- Restore Matomo --- START
  useEffect(() => {
    window._mtm = window._mtm || [];
    window._mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
    
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://log.definedlearning.com/js/container_TPxL94eD.js';
    
    document.head.appendChild(script);
    
    return () => {
      try {
         if (script.parentNode) {
      document.head.removeChild(script);
         }
      } catch (e) {
          console.warn('Error removing Matomo script:', e);
      }
    };
  }, []);
  // --- Restore Matomo --- END

  // --- Restore Portal Fetch/Cache Logic --- START
  useEffect(() => {
    const fetchPortalData = async () => {
      try {
        const response = await fetch('/api/portal/current');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        if (data) {
          setPortalData(data);
          setIsPortalDomain(true);
          window.__INITIAL_PORTAL_DATA__ = data;
        } else {
          setPortalData(null);
          setIsPortalDomain(false);
          window.__INITIAL_PORTAL_DATA__ = null;
        }
      } catch (error) {
        console.error('Error fetching portal data:', error);
        setPortalData(null);
        setIsPortalDomain(false);
        window.__INITIAL_PORTAL_DATA__ = null;
      } finally {
        setLoading(false);
      }
    };

    if (!window.__INITIAL_PORTAL_DATA__ || location.pathname === '/') {
      fetchPortalData();
    } else {
      setPortalData(window.__INITIAL_PORTAL_DATA__);
      setIsPortalDomain(!!window.__INITIAL_PORTAL_DATA__); 
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.__INITIAL_PORTAL_DATA__) {
      if (!portalData || portalData.id !== window.__INITIAL_PORTAL_DATA__.id) {
      setPortalData(window.__INITIAL_PORTAL_DATA__);
      }
      if (!isPortalDomain) {
      setIsPortalDomain(true);
      }
    } else {
      if (portalData) {
          setPortalData(null);
      }
      if (isPortalDomain) {
          setIsPortalDomain(false);
      }
    }
  }, [location.pathname]);
  // --- Restore Portal Fetch/Cache Logic --- END

  // Helper to determine if we're on a portal-specific page
  const isPortalPage = () => {
    return location.pathname === '/' || location.pathname.startsWith('/p/');
  };

  // Helper to check if the current route is a virtual internship route
  const isVirtualInternshipPage = () => {
    return location.pathname.startsWith('/virtual-internship/');
  };

  // Helper to get header props
  const getHeaderProps = () => {
    // Restore portal logic
    if (isPortalDomain && portalData?.settings) {
      return {
        logo: portalData.settings.logo_url,
        theme: portalData.settings.theme
      };
    }
    return {};
  };

  // +++ Define DashboardDispatcher Component +++
  const DashboardDispatcher = () => {
    const { isStudent, currentUser, token, isInPreviewMode } = useAuth(); // Added isInPreviewMode

    if (!token || !currentUser) {
      return <Loading />;
    }

    // If in preview mode, always show MyActivities
    if (isInPreviewMode) {
      return <MyActivities />;
    }

    // Otherwise, use the existing logic based on isStudent
    if (isStudent) { 
      return <MyActivities />;
    } else {
      return <EducatorDashboard />;
    } 
  };
  // +++ End DashboardDispatcher Component +++

  if (loading) { // Restore loading check
    return <Loading />;
  }

  return (
    <div className={`d-flex flex-column min-vh-100 ${isMinimal ? 'layout-minimal' : ''}`}>
      {/* Header Logic - Now Controlled by Layout Context */}
      {showHeader && (!isPortalDomain || !isPortalPage()) && !isVirtualInternshipPage() && (
          <React.Fragment>
              <Header {...getHeaderProps()} />
          </React.Fragment>
      )}
      {/* Only show AdminBreadcrumb on admin routes and when header is visible */}
      {showHeader && location.pathname.startsWith('/admin') && location.pathname !== '/admin' && (
           <React.Fragment>
              <AdminBreadcrumb />
          </React.Fragment>
      )}
      
      <main className={`flex-grow-1 ${isVirtualInternshipPage() ? 'p-0' : ''}`}>
        <Suspense fallback={<Loading />}>
          <Routes>
             {/* --- Restore Root Route Logic --- */}
            <Route path="/" element={
              isPortalDomain ? (
                <React.Fragment>
                  <PortalRouter portalData={portalData} />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Home />
                </React.Fragment>
              )
            } />
             {/* --- Restore All Other Routes --- */}
            <Route path="/p/:slug/*" element={<PortalRouter portalData={portalData} />} />
            <Route path="/pages/:slug" element={<PageContent />} />
            <Route path="/jobs" element={<JobBoard />} />
            <Route path="/job-board" element={<JobBoard />} />
            <Route path="/jobs/:id" element={<JobListing />} />
            <Route path="/job-listing/:id" element={<JobListing />} />
            <Route path="/collections/:id" element={<CollectionDetail />} />
            <Route path="/virtual-internship" element={<VirtualInternship />} />
            <Route path="/virtual-internships" element={<VirtualInternshipBoard />} />
            <Route path="/virtual-internship/:internshipId" element={<VirtualInternshipMain />} />
            <Route path="/virtual-internship/:internshipId/location/:locationId" element={<VirtualInternshipMain />} />
            <Route path="/virtual-internship/:internshipId/activity/:activityId" element={<ActivityPlayer />} />

            <Route path="/interview" element={
              <ProtectedRoute>
                <Interview />
              </ProtectedRoute>
            } />
            <Route path="/interview/:id" element={
              <ProtectedRoute>
                <Interview />
              </ProtectedRoute>
            } />

            <Route path="/feedback/:referenceId" element={<InterviewFeedback />} />
            <Route path="/interview-report/:referenceId" element={<InterviewReport />} />
            <Route path="/career-courses" element={<CareerCourses />} />
            <Route path="/apply/:id" element={<JobApplication />} />
            <Route path="/career-coach" element={<CareerCoach />} />
            <Route path="/career-tools" element={<CareerTools />} />
            <Route path="/tools/interview-prep" element={<InterviewPrep />} />
            <Route path="/tools/networking" element={<NetworkingGuide />} />
            <Route path="/tools/elevator-pitch" element={<ElevatorPitchBuilder />} />
            <Route path="/tools/elevator-pitch-builder" element={<ElevatorPitchBuilder />} />
            <Route path="/tools/network-builder" element={<NetworkBuilder />} />
            <Route path="/companies" element={<CompanyBoard />} />
            <Route path="/companies/:id" element={<CompanyDetail />} />
            <Route path="/companyhome/:id" element={<CompanyHome />} />
            <Route path="/company-board" element={<CompanyBoard />} />
            <Route path="/industries/:id" element={<IndustryDetail />} />
            <Route path="/login" element={<LoginTypeSelection />} />
            <Route path="/login/student" element={<StudentLoginForm />} />
            <Route path="/login/educator" element={<UserLoginForm />} />
            <Route path="/auth/callback" element={<AuthTokenHandler />} />
            <Route path="/activity-test" element={<ActivityTestPage />} />
            <Route path="/activity/:activityId" element={<ActivityPlayer isStandalone={true} />} />
            <Route path="/activity-test/:activityId" element={<ActivityPlayer isStandalone={true} />} />
            <Route path="/career-assessment" element={<CareerAssessment />} />
            <Route path="/assessment/results/:resultId" element={<CareerAssessmentResults />} />
            <Route path="/career-assessment-results/:resultUuid" element={<CareerAssessmentResults />} />
            <Route path="/career-assessment-report" element={<ProtectedRoute><CareerAssessmentReport /></ProtectedRoute>} />
            <Route path="/training-opportunities" element={<TrainingOpportunities />} />
            <Route path="/career-opportunities/:careerIdentifier" element={<CareerOpportunities />} />
            <Route path="/education-planner" element={<EducationPlanner />} />

            {/* Protected Student Route - MODIFIED to use DashboardDispatcher */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardDispatcher />
                </ProtectedRoute>
              } 
            />
            
            {/* Add new route for Track Progress */}
            <Route 
              path="/track-progress"
              element={
                <ProtectedRoute>
                  <TrackProgress />
                </ProtectedRoute>
              }
            />

            {/* --- Admin Routes --- */}
            {/* Wrap all admin routes requiring authentication and admin privileges */}
            <Route path="/admin" element={<AdminLogin />} /> {/* Login page is public */}
            
            {/* Routes below require admin login */}
            <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly={true}><AdminDashboard /></ProtectedRoute>} />
            
            <Route path="/admin/jobs" element={<ProtectedRoute adminOnly={true}><JobList /></ProtectedRoute>} />
            <Route path="/admin/jobs/new" element={<ProtectedRoute adminOnly={true}><JobEditor /></ProtectedRoute>} />
            <Route path="/admin/jobs/:id/edit" element={<ProtectedRoute adminOnly={true}><JobEditor /></ProtectedRoute>} />
            <Route path="/admin/jobs/edit/:id" element={<ProtectedRoute adminOnly={true}><JobEditor /></ProtectedRoute>} />
            <Route path="/admin/jobs/:jobId/questions" element={<ProtectedRoute adminOnly={true}><QuestionEditor /></ProtectedRoute>} />
            <Route path="/admin/jobs/upload" element={<ProtectedRoute adminOnly={true}><JobDataUpload /></ProtectedRoute>} />
            
            <Route path="/admin/companies" element={<ProtectedRoute adminOnly={true}><CompanyList /></ProtectedRoute>} />
            <Route path="/admin/companies/new" element={<ProtectedRoute adminOnly={true}><CompanyEditor /></ProtectedRoute>} />
            <Route path="/admin/companies/:id/edit" element={<ProtectedRoute adminOnly={true}><CompanyEditor /></ProtectedRoute>} />
            
            <Route path="/admin/industries" element={<ProtectedRoute adminOnly={true}><IndustriesPage /></ProtectedRoute>} />
            <Route path="/admin/industries/new" element={<ProtectedRoute adminOnly={true}><IndustryEditor /></ProtectedRoute>} />
            <Route path="/admin/industries/:id/edit" element={<ProtectedRoute adminOnly={true}><IndustryEditor /></ProtectedRoute>} />
            {/* Note: /admin/industries/:id might be public or need different logic? Assuming admin only for now. */}
            <Route path="/admin/industries/:id" element={<ProtectedRoute adminOnly={true}><IndustryDetail /></ProtectedRoute>} /> 
            
            <Route path="/admin/careers" element={<ProtectedRoute adminOnly={true}><CareerList /></ProtectedRoute>} />
            <Route path="/admin/careers/:id/edit" element={<ProtectedRoute adminOnly={true}><CareerEdit /></ProtectedRoute>} />
            <Route path="/admin/careers/new" element={<ProtectedRoute adminOnly={true}><CareerEdit /></ProtectedRoute>} />
            <Route path="/admin/collections" element={<ProtectedRoute adminOnly={true}><CollectionList /></ProtectedRoute>} />
            <Route path="/admin/collections/new" element={<ProtectedRoute adminOnly={true}><CollectionEdit /></ProtectedRoute>} />
            {/* Consolidate /admin/collections/:id and /edit */}
            <Route path="/admin/collections/:id/edit" element={<ProtectedRoute adminOnly={true}><CollectionEdit /></ProtectedRoute>} />
            <Route path="/admin/collections/:id/manage" element={<ProtectedRoute adminOnly={true}><CollectionManage /></ProtectedRoute>} />
             {/* Removed redundant /admin/collections/:id route, assuming edit covers viewing */} 
            
            <Route path="/admin/courses" element={<ProtectedRoute adminOnly={true}><CourseList /></ProtectedRoute>} />
            <Route path="/admin/courses/new" element={<ProtectedRoute adminOnly={true}><CourseEditor /></ProtectedRoute>} />
            <Route path="/admin/courses/:id/edit" element={<ProtectedRoute adminOnly={true}><CourseEditor /></ProtectedRoute>} />
            <Route path="/admin/courses/:id/player" element={<ProtectedRoute adminOnly={true}><CoursePlayer /></ProtectedRoute>} />
            
            {/* Updated Lesson Library and Editor Routes */}
            <Route path="/admin/lessons" element={<ProtectedRoute adminOnly={true}><LessonList /></ProtectedRoute>} />
            <Route path="/admin/lesson-editor/new" element={<ProtectedRoute adminOnly={true}><LessonEditor /></ProtectedRoute>} />
            <Route path="/admin/lesson-editor/:lessonId" element={<ProtectedRoute adminOnly={true}><LessonEditor /></ProtectedRoute>} />
            
            {/* Section Library and Editor Routes */}
            <Route path="/admin/sections" element={<ProtectedRoute adminOnly={true}><SectionList /></ProtectedRoute>} />
            <Route path="/admin/sections/new" element={<ProtectedRoute adminOnly={true}><SectionEditor /></ProtectedRoute>} />
            <Route path="/admin/sections/edit/:sectionId" element={<ProtectedRoute adminOnly={true}><SectionEditor /></ProtectedRoute>} />

            <Route path="/admin/course-generator" element={<ProtectedRoute adminOnly={true}><CourseGenerator /></ProtectedRoute>} />

            <Route path="/admin/partners" element={<ProtectedRoute adminOnly={true}><PartnerList /></ProtectedRoute>} />
            <Route path="/admin/partners/new" element={<ProtectedRoute adminOnly={true}><PartnerEditor /></ProtectedRoute>} />
            <Route path="/admin/partners/:id/edit" element={<ProtectedRoute adminOnly={true}><PartnerEditor /></ProtectedRoute>} />

            <Route path="/admin/portals" element={<ProtectedRoute adminOnly={true}><PortalList /></ProtectedRoute>} />
            <Route path="/admin/portals/new" element={<ProtectedRoute adminOnly={true}><PortalEditor /></ProtectedRoute>} />
            <Route path="/admin/portals/edit/:id" element={<ProtectedRoute adminOnly={true}><PortalEditor /></ProtectedRoute>} />
            <Route path="/admin/pages" element={<ProtectedRoute adminOnly={true}><PageManager /></ProtectedRoute>} />
            
            <Route path="/admin/activities" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><ActivityManagement /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/admin/activities/new" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><ActivityBuilder /></ErrorBoundary></ProtectedRoute>} />
            {/* Note: Need to handle nested routes/params correctly within ProtectedRoute */}
            <Route path="/admin/activities/:id/edit" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><ActivityEditor /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/admin/activities/:id/preview" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><ActivityEditor preview={true} /></ErrorBoundary></ProtectedRoute>} />

            <Route path="/admin/simulations" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><SimulationList /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/admin/simulations/new" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><SimulationEditor /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/admin/simulations/:id/edit" element={<ProtectedRoute adminOnly={true}><ErrorBoundary><SimulationEditor /></ErrorBoundary></ProtectedRoute>} />

            <Route path="/admin/badges" element={<ProtectedRoute adminOnly={true}><BadgeList /></ProtectedRoute>} />
            <Route path="/admin/badges/new" element={<ProtectedRoute adminOnly={true}><BadgeEditor /></ProtectedRoute>} />
            <Route path="/admin/badges/:id/edit" element={<ProtectedRoute adminOnly={true}><BadgeEditor /></ProtectedRoute>} />
            <Route path="/admin/badges/:id/award" element={<ProtectedRoute adminOnly={true}><BadgeAwardManager /></ProtectedRoute>} />
            <Route path="/admin/badge-assertions" element={<ProtectedRoute adminOnly={true}><BadgeAssertionList /></ProtectedRoute>} />
            
            {/* Public badge verification route, no protection needed here */}
            <Route path="/badges/verify/:id" element={<BadgeVerification />} />

            {/* Admin Virtual Internship Routes */}
            <Route path="/admin/virtual-internships" element={<ProtectedRoute adminOnly={true}><InternshipList /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/new" element={<ProtectedRoute adminOnly={true}><InternshipBuilder /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId" element={<ProtectedRoute adminOnly={true}><VirtualInternshipDashboard /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/edit" element={<ProtectedRoute adminOnly={true}><InternshipBuilder /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/locations" element={<ProtectedRoute adminOnly={true}><LocationList /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/locations/new" element={<ProtectedRoute adminOnly={true}><LocationEditor /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/locations/:locationId" element={<ProtectedRoute adminOnly={true}><LocationEditor /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/locations/:locationId/hotspots" element={<ProtectedRoute adminOnly={true}><HotspotEditor /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/activities/:activityId" element={<ProtectedRoute adminOnly={true}><ActivityEditor /></ProtectedRoute>} />
            <Route path="/admin/virtual-internships/:internshipId/locations/:locationId/hotspots/:hotspotId/activity/new" element={<ProtectedRoute adminOnly={true}><ActivityEditor /></ProtectedRoute>} />

            {/* Public preview route? Needs clarification. Assuming public for now. */}
            <Route path="/preview/activity" element={<ActivityPreview />} /> 

            <Route path="/admin/students" element={<ProtectedRoute adminOnly={true}><ManageStudents /></ProtectedRoute>} />

            <Route path="/admin/reports" element={<ProtectedRoute adminOnly={true}><ReportDashboard /></ProtectedRoute>} />
            
            {/* --- End Admin Routes --- */}
          </Routes>
        </Suspense>
      </main>
      
      {/* Footer Logic - Now Controlled by Layout Context */}
      {showFooter && (!isPortalDomain || !isPortalPage()) && !isVirtualInternshipPage() && (
          <React.Fragment>
              <Footer />
          </React.Fragment>
      )}
      
      {/* Minimal Footer for Tool-Only Mode */}
      {showMinimalFooter && (
          <MinimalFooter />
      )}
    </div>
  );
};

// Main App component
function App() {
  return (
    <ErrorBoundary>
        <Router>
      <AppContent />
        </Router>
    </ErrorBoundary>
  );
}

export default App;
